import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ExternalLink, Save, RefreshCw, Image as ImageIcon, Trash2 } from "lucide-react";

const SITE = "https://www.elevateglobalsolutions.net";
const ROUTES = ["/", "/about", "/services", "/coaching", "/blog", "/contact", "/elevate-moment", "/book-appointment"];

type LiveTags = {
  title: string | null;
  description: string | null;
  ogTitle: string | null;
  ogDescription: string | null;
  ogImage: string | null;
  ogUrl: string | null;
  canonical: string | null;
};

const parseHead = (html: string): LiveTags => {
  const m = (re: RegExp) => { const x = html.match(re); return x ? x[1].trim() : null; };
  return {
    title: m(/<title[^>]*>([\s\S]*?)<\/title>/i),
    description: m(/<meta[^>]+name=["']description["'][^>]+content=["']([^"']+)["']/i),
    ogTitle: m(/<meta[^>]+property=["']og:title["'][^>]+content=["']([^"']+)["']/i),
    ogDescription: m(/<meta[^>]+property=["']og:description["'][^>]+content=["']([^"']+)["']/i),
    ogImage: m(/<meta[^>]+property=["']og:image["'][^>]+content=["']([^"']+)["']/i),
    ogUrl: m(/<meta[^>]+property=["']og:url["'][^>]+content=["']([^"']+)["']/i),
    canonical: m(/<link[^>]+rel=["']canonical["'][^>]+href=["']([^"']+)["']/i),
  };
};

const SharePreview = ({ url, title, description, image }: { url: string; title: string; description: string; image: string | null }) => {
  const host = (() => { try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return url; } })();
  return (
    <div className="rounded-lg overflow-hidden border border-border/40 bg-background max-w-md">
      {image ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={image} alt="OG preview" className="w-full aspect-[1.91/1] object-cover bg-muted" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
      ) : (
        <div className="w-full aspect-[1.91/1] bg-muted flex items-center justify-center text-foreground/40">
          <ImageIcon className="h-8 w-8" />
        </div>
      )}
      <div className="p-3 bg-muted/40">
        <p className="text-[11px] uppercase text-foreground/50 tracking-wide">{host}</p>
        <p className="text-sm font-semibold mt-1 line-clamp-2">{title || "(no title)"}</p>
        <p className="text-xs text-foreground/60 mt-1 line-clamp-2">{description || "(no description)"}</p>
      </div>
    </div>
  );
};

const OgPreviewPanel = () => {
  const [path, setPath] = useState("/");
  const [loadingLive, setLoadingLive] = useState(false);
  const [live, setLive] = useState<LiveTags | null>(null);
  const [saving, setSaving] = useState(false);

  // override form
  const [ogTitle, setOgTitle] = useState("");
  const [ogDescription, setOgDescription] = useState("");
  const [ogImage, setOgImage] = useState("");
  const [ogUrl, setOgUrl] = useState("");
  const [hasRow, setHasRow] = useState(false);

  const loadOverride = async (p: string) => {
    const { data } = await supabase
      .from("seo_overrides")
      .select("og_title,og_description,og_image,og_url")
      .eq("path", p)
      .maybeSingle();
    const row = data as any;
    setHasRow(!!row);
    setOgTitle(row?.og_title ?? "");
    setOgDescription(row?.og_description ?? "");
    setOgImage(row?.og_image ?? "");
    setOgUrl(row?.og_url ?? `${SITE}${p === "/" ? "" : p}`);
  };

  const loadLive = async (p: string) => {
    setLoadingLive(true);
    setLive(null);
    try {
      const url = `${SITE}${p === "/" ? "" : p}`;
      const proxied = `https://r.jina.ai/${url}`;
      // Try direct first, then a CORS-friendly text mirror as fallback
      let html = "";
      try {
        const res = await fetch(url, { mode: "cors" });
        html = await res.text();
      } catch {
        const res = await fetch(proxied);
        html = await res.text();
      }
      setLive(parseHead(html));
    } catch (e: any) {
      toast.error(`Could not fetch live tags: ${e.message}`);
    } finally {
      setLoadingLive(false);
    }
  };

  useEffect(() => { void loadOverride(path); void loadLive(path); }, [path]);

  const save = async () => {
    setSaving(true);
    const payload = {
      path,
      og_title: ogTitle || null,
      og_description: ogDescription || null,
      og_image: ogImage || null,
      og_url: ogUrl || null,
    };
    const { error } = hasRow
      ? await supabase.from("seo_overrides").update(payload).eq("path", path)
      : await supabase.from("seo_overrides").insert(payload);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success("OG override saved. Republish for social crawlers to see it.");
    setHasRow(true);
  };

  const clearOverride = async () => {
    const { error } = await supabase
      .from("seo_overrides")
      .update({ og_title: null, og_description: null, og_image: null, og_url: null })
      .eq("path", path);
    if (error) return toast.error(error.message);
    setOgTitle(""); setOgDescription(""); setOgImage(""); setOgUrl(`${SITE}${path === "/" ? "" : path}`);
    toast.success("OG override cleared.");
  };

  const previewTitle = ogTitle || live?.ogTitle || live?.title || "";
  const previewDesc = ogDescription || live?.ogDescription || live?.description || "";
  const previewImage = ogImage || live?.ogImage || null;
  const previewUrl = ogUrl || live?.ogUrl || `${SITE}${path === "/" ? "" : path}`;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex-1">
          <Label className="text-xs text-foreground/60">Page</Label>
          <select
            className="w-full bg-background border border-border/40 rounded px-3 py-2 text-sm mt-1"
            value={path}
            onChange={(e) => setPath(e.target.value)}
          >
            {ROUTES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <Button variant="outline" size="sm" onClick={() => loadLive(path)} disabled={loadingLive}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loadingLive ? "animate-spin" : ""}`} /> Refetch live
        </Button>
        <a
          href={`https://www.opengraph.xyz/url/${encodeURIComponent(`${SITE}${path === "/" ? "" : path}`)}`}
          target="_blank" rel="noreferrer"
          className="inline-flex items-center text-sm text-primary hover:underline"
        >
          Open external check <ExternalLink className="h-3 w-3 ml-1" />
        </a>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Live tags */}
        <Card className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold">Live tags (published)</h4>
            {hasRow && <Badge variant="outline" className="text-[10px]">Override active</Badge>}
          </div>
          {loadingLive && <p className="text-xs text-foreground/50">Loading…</p>}
          {!loadingLive && live && (
            <div className="space-y-2 text-xs">
              <Row k="title" v={live.title} />
              <Row k="description" v={live.description} />
              <Row k="og:title" v={live.ogTitle} />
              <Row k="og:description" v={live.ogDescription} />
              <Row k="og:image" v={live.ogImage} link />
              <Row k="og:url" v={live.ogUrl} link />
              <Row k="canonical" v={live.canonical} link />
            </div>
          )}
        </Card>

        {/* Preview */}
        <Card className="p-4 space-y-3">
          <h4 className="text-sm font-semibold">Social share preview</h4>
          <SharePreview url={previewUrl} title={previewTitle} description={previewDesc} image={previewImage} />
          <p className="text-[11px] text-foreground/50">
            This is how the link will appear on LinkedIn, Slack, Facebook, X, etc. Image should be ~1200×630.
          </p>
        </Card>
      </div>

      {/* Edit override */}
      <Card className="p-4 space-y-3">
        <h4 className="text-sm font-semibold">Override OG tags for <code className="text-primary">{path}</code></h4>
        <p className="text-xs text-foreground/50">
          Empty fields fall back to the page&apos;s default tags. After saving, republish the site so social crawlers pick up the change.
        </p>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">og:title</Label>
            <Input value={ogTitle} onChange={(e) => setOgTitle(e.target.value)} placeholder="Title shown on social cards" />
          </div>
          <div>
            <Label className="text-xs">og:url</Label>
            <Input value={ogUrl} onChange={(e) => setOgUrl(e.target.value)} placeholder="https://www.elevateglobalsolutions.net/…" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">og:description</Label>
            <Textarea rows={2} value={ogDescription} onChange={(e) => setOgDescription(e.target.value)} placeholder="Short, punchy description" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">og:image (absolute URL, ~1200×630)</Label>
            <Input value={ogImage} onChange={(e) => setOgImage(e.target.value)} placeholder="https://…/share.jpg" />
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={save} disabled={saving}>
            <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save override"}
          </Button>
          {hasRow && (
            <Button variant="outline" onClick={clearOverride}>
              <Trash2 className="h-4 w-4 mr-2" /> Clear OG fields
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

const Row = ({ k, v, link }: { k: string; v: string | null; link?: boolean }) => (
  <div className="flex gap-2 border-b border-border/20 pb-1.5">
    <span className="text-foreground/50 w-28 shrink-0">{k}</span>
    {v ? (
      link ? <a href={v} target="_blank" rel="noreferrer" className="text-primary hover:underline break-all">{v}</a>
           : <span className="break-all">{v}</span>
    ) : <span className="text-destructive/70 italic">missing</span>}
  </div>
);

export default OgPreviewPanel;
